import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, MapPin, Search, Sparkles, HeartHandshake, ArrowRight,
  Gift, CalendarPlus, Share2, ImagePlus, Globe, Megaphone, Wand2, UserPlus,
  TrendingUp, BadgeCheck, Rocket, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import AssociationsMap from "@/components/AssociationsMap";
import { SpaceAccessBar } from "@/components/SpaceAccessBar";

const ASSO_BENEFITS = [
  { icon: Gift, title: "Des réductions exclusives pour vos adhérents",
    desc: "Chaque membre accède à tous les bons plans et avantages négociés chez les commerçants partenaires d'Eure-et-Loir. Un vrai cadeau qui donne envie d'adhérer… et de rester." },
  { icon: CalendarPlus, title: "Vos événements sur l'agenda des Sorties",
    desc: "Créez vos lotos, tournois, concerts ou assemblées en 2 minutes. Ils s'affichent instantanément sur l'agenda public « Sorties & Événements » vu par tout le département." },
  { icon: Share2, title: "Tout devient partageable en 1 clic",
    desc: "Chaque événement et chaque actu génère un lien partageable prêt à diffuser par SMS, WhatsApp, email ou sur vos pages — sans effort, sans graphiste." },
  { icon: ImagePlus, title: "Des visuels prêts à poster, générés par IA",
    desc: "Affiches et posts avec images percutantes créés automatiquement pour vos réseaux sociaux et vos groupes Facebook. Vous publiez, ça claque, à chaque fois." },
  { icon: Megaphone, title: "Relayé dans nos groupes & réseaux locaux",
    desc: "Vos actus et événements peuvent être diffusés dans notre réseau de groupes Facebook locaux et sur nos canaux. Une caisse de résonance que personne n'a au niveau local." },
  { icon: Globe, title: "Une page vitrine gratuite, trouvable sur Google",
    desc: "Logo, photos, description, contacts, agenda : une vraie page professionnelle optimisée pour le référencement local. Les habitants vous trouvent enfin facilement." },
  { icon: MapPin, title: "Visible sur la carte interactive du 28",
    desc: "Votre club apparaît sur la carte des clubs, commune par commune. Les nouveaux habitants vous repèrent en un coup d'œil." },
  { icon: UserPlus, title: "Recrutez adhérents et bénévoles",
    desc: "Plus de visibilité = plus d'inscriptions. On vous met en avant auprès de milliers d'habitants qui cherchent justement un club comme le vôtre." },
  { icon: Wand2, title: "L'IA rédige pour vous",
    desc: "Descriptions, actualités, mots-clés SEO : notre assistant IA écrit des textes clairs et attractifs à votre place. Fini la page blanche." },
  { icon: TrendingUp, title: "Des statistiques claires",
    desc: "Suivez les vues, les partages et l'intérêt pour vos événements en temps réel. Vous savez enfin ce qui fonctionne." },
];

export default function Clubs() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [mapPoints, setMapPoints] = useState([]);
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const directoryRef = useRef(null);

  const selectCommune = (name) => {
    setCity(name);
    setTimeout(() => directoryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
  };

  useSeo({
    title: "Clubs sportifs et culturels en Eure-et-Loir | PROXIreduc-28",
    description: "Découvrez les clubs locaux d'Eure-et-Loir : sport, culture, solidarité, loisirs. Pages vitrines gratuites, événements et actualités.",
    canonicalPath: "/clubs",
  });

  useEffect(() => {
    api.get("/clubs/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
    api.get("/public/clubs/map").then((r) => setMapPoints(r.data.points || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (city) p.set("city", city);
    if (cat) p.set("category", cat);
    if (q) p.set("q", q);
    try {
      const { data } = await api.get(`/public/clubs?${p.toString()}`);
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [city, cat, q]);

  return (
    <div data-testid="clubs-page">
      <SpaceAccessBar to="/clubs/inscription" label="Référencer mon club — gratuit"
        note="Vous gérez un club ? Créez votre espace club en quelques minutes." testId="club-access-bar"
        spaceTo="/espace-club" spaceLabel="Accéder à mon espace club" />
      <section className="bg-gradient-to-br from-indigo-50 via-white to-orange-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-800 text-xs font-bold uppercase tracking-widest">
            <Users size={14} /> Vie des clubs locaux
          </div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900" data-testid="clubs-title">
            Les <span className="text-indigo-700">clubs</span> d'Eure-et-Loir
          </h1>
          <p className="mt-3 text-slate-600 md:text-lg max-w-2xl">
            Clubs sportifs, culturels, solidaires… Trouvez un club près de chez vous, suivez ses événements et ses actualités.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/clubs/inscription">
              <Button className="btn-shine text-white" data-testid="clubs-register-cta">
                <HeartHandshake className="w-4 h-4 mr-1.5" /> Référencer mon club — gratuit
              </Button>
            </Link>
            <Link to="/sortir?scope=club">
              <Button variant="outline" data-testid="clubs-agenda-cta">Agenda des clubs</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-100" data-testid="clubs-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-3xl mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-xs font-bold uppercase tracking-widest">
              <Rocket size={14} /> 100 % gratuit · sans engagement
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">
              Rejoindre PROXIreduc-28, c'est offrir à votre club <span className="text-indigo-700">ce qui se fait de mieux</span> :
            </h2>
          </div>
          <div className="relative rounded-3xl overflow-hidden mb-12 shadow-lg" data-testid="club-benefits-banner">
            <img src="https://static.prod-images.emergentagent.com/jobs/66457276-1572-4d20-83d0-d7f4ad854fc6/images/e23ff81c2e4ec82e7f26ad63e5db6801be75d75da6828d830f68f2e003e6cc96.jpeg"
                 alt="La vie des clubs en Eure-et-Loir" loading="lazy"
                 className="w-full h-56 sm:h-72 md:h-80 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-indigo-950/80 via-indigo-900/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <p className="font-heading text-white text-xl sm:text-2xl md:text-3xl font-extrabold drop-shadow-md max-w-2xl leading-tight">
                La vie des clubs d'Eure-et-Loir mérite ce qu'il y a de mieux.
              </p>
            </div>
          </div>
          <div className="max-w-3xl">
            <p className="text-slate-600 md:text-lg">
              On ne vous propose pas une simple fiche. On vous donne une véritable machine à visibilité, à adhésions et à avantages — gratuitement, pour tous les clubs d'Eure-et-Loir d'Eure-et-Loir.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ASSO_BENEFITS.map((b, i) => (
              <div key={i} data-testid={`club-benefit-${i}`}
                   className="group relative bg-gradient-to-br from-white to-indigo-50/40 rounded-2xl border border-slate-200 p-6 transition-all hover:border-indigo-300 hover:shadow-lg hover:-translate-y-0.5">
                <div className="w-12 h-12 rounded-xl bg-indigo-600 text-white grid place-items-center shadow-sm group-hover:scale-110 transition-transform">
                  <b.icon size={22} />
                </div>
                <h3 className="mt-4 font-heading font-bold text-slate-900 leading-snug">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-3xl bg-gradient-to-r from-indigo-700 to-indigo-600 text-white p-8 md:p-10 overflow-hidden relative">
            <div className="relative z-10 md:flex md:items-center md:justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold">Tout ça, 0 € — vraiment.</h3>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-indigo-50 text-sm">
                  {["Page vitrine référencée sur Google","Avantages pour tous vos adhérents","Événements sur l'agenda des Sorties","Visuels & posts partageables par IA","Relais dans nos groupes Facebook locaux","Aucune carte bancaire, aucun engagement"].map((t, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle2 size={18} className="shrink-0 mt-0.5 text-white" /> {t}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 md:mt-0 shrink-0">
                <Link to="/clubs/inscription">
                  <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold shadow-xl w-full md:w-auto" data-testid="club-benefits-cta">
                    <BadgeCheck className="w-5 h-5 mr-2" /> Référencer mon club — gratuit
                  </Button>
                </Link>
                <p className="mt-3 text-center text-indigo-100 text-xs">Prêt en 2 minutes · Rejoignez la vie des clubs du 28</p>
              </div>
            </div>
          </div>
        </div>
      </section>


        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-testid="clubs-map-section">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={18} className="text-indigo-700" />
            <h2 className="font-heading font-bold text-lg text-slate-900">Les clubs près de chez vous</h2>
          </div>
          <AssociationsMap points={mapPoints} onCommuneSelect={selectCommune} />
        </section>

      <section ref={directoryRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 scroll-mt-20" data-testid="clubs-directory">
        <div className="grid md:grid-cols-[1fr_auto_auto] gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un club…"
                   className="pl-9" data-testid="clubs-search" />
          </div>
          <Select value={cat} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
            <SelectTrigger className="md:w-56" data-testid="clubs-cat"><SelectValue placeholder="Toutes catégories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {cats.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
            <SelectTrigger className="md:w-48" data-testid="clubs-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              {cities.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((a) => (
            <Link key={a.id} to={`/clubs/${a.slug}`} data-testid={`club-card-${a.id}`}
                  className="card-hover group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
              <div className="relative aspect-[16/9] bg-slate-100">
                {a.cover_url ? (
                  <img src={a.cover_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-indigo-200"><Users size={48} /></div>
                )}
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold bg-white/90 text-indigo-800">{a.category_label}</span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-3">
                  {a.logo_url && <img src={a.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-100" />}
                  <h3 className="font-heading font-semibold text-slate-900 leading-snug line-clamp-2">{a.name}</h3>
                </div>
                {a.description && <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.description}</p>}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1"><MapPin size={12} /> {a.city || "Eure-et-Loir"}</span>
                  <span className="inline-flex items-center gap-1 text-indigo-700 font-semibold group-hover:gap-1.5 transition-all">Voir <ArrowRight size={12} /></span>
                </div>
              </div>
            </Link>
          ))}
          {!loading && items.length === 0 && (
            <div className="col-span-full bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="clubs-empty">
              <Sparkles size={30} className="mx-auto text-slate-300 mb-2" />
              Aucun club ne correspond à votre recherche pour le moment.
              <div className="mt-3"><Link to="/clubs/inscription" className="text-indigo-700 font-semibold hover:underline">Soyez le premier à vous référencer →</Link></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
